import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, signal } from '@angular/core';
import { ApiService } from '../../services/api.service';
import { SessionService } from '../../services/session.service';
import { runSyncOrchestration, SyncProgress } from '../../shared/sync-orchestrator.client';
import { DashboardHeaderComponent } from './components/dashboard-header/dashboard-header.component';
import { DashboardPeriodFilterComponent } from './components/dashboard-period-filter/dashboard-period-filter.component';
import { ProductEconomicsCardComponent } from './components/product-economics-card/product-economics-card.component';
import type {
  DashboardMonthOption,
  EmployeeConfig,
  InsightsOverview,
  MercadoLivreStatus,
  MpAccountMoneyView,
  PeriodBusinessView,
  PeriodClosure,
  SyncJobProgress,
  SummaryResponse
} from '../../models/api.models';

type DashboardTab = 'caixa' | 'movimentos' | 'vendas' | 'historico';
type CommissionAdjustmentGroup = {
  key: string;
  orderId: string | null;
  sourceId: string | null;
  itemTitle: string | null;
  lastSettlementDate: string | null;
  rowsCount: number;
  debits: number;
  credits: number;
  net: number;
  reasons: string;
};

const DEFAULT_EMPLOYEES: EmployeeConfig[] = [
  {
    name: 'Rodrigo',
    pix_key: null,
    commission_rate: 0.03,
    calculation_base: 'LÍQUIDO',
    status: 'ACTIVE'
  },
  {
    name: 'Maria',
    pix_key: null,
    commission_rate: 0.01,
    calculation_base: 'LÍQUIDO',
    status: 'ACTIVE'
  }
];

function toDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setUTCDate(copy.getUTCDate() + days);
  return copy;
}

function subtractOneYear(dateOnly: string): string {
  const date = new Date(`${dateOnly}T00:00:00.000Z`);
  date.setUTCFullYear(date.getUTCFullYear() - 1);
  return toDateOnly(date);
}

function monthFromDateRange(startDate: string, endDate: string): string {
  if (!/^\d{4}-\d{2}-01$/.test(startDate)) {
    return 'manual';
  }
  const [year, month] = startDate.split('-').map(Number);
  const end = new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
  return endDate === end ? startDate.slice(0, 7) : 'manual';
}

function defaultClosedPeriod(): { startDate: string; endDate: string } {
  const today = new Date();
  const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const day = utcToday.getUTCDay();
  const daysSinceFriday = (day + 2) % 7;
  const end = addDays(utcToday, daysSinceFriday === 0 ? 0 : -daysSinceFriday);
  const start = addDays(end, -6);
  return {
    startDate: toDateOnly(start),
    endDate: toDateOnly(end)
  };
}

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [CommonModule, DashboardHeaderComponent, DashboardPeriodFilterComponent, ProductEconomicsCardComponent],
  templateUrl: './dashboard-page.component.html',
  styleUrl: './dashboard-page.component.css'
})
export class DashboardPageComponent {
  @ViewChild('settingsPanel') private settingsPanel?: ElementRef<HTMLElement>;

  private readonly defaultPeriod = defaultClosedPeriod();
  private refreshSeq = 0;
  private settingsReturnFocus: HTMLElement | null = null;
  readonly printGeneratedAt = signal(new Date());

  readonly tab = signal<DashboardTab>('caixa');
  readonly startDate = signal(this.defaultPeriod.startDate);
  readonly endDate = signal(this.defaultPeriod.endDate);
  readonly selectedMonth = signal(monthFromDateRange(this.defaultPeriod.startDate, this.defaultPeriod.endDate));
  readonly monthOptions = signal<DashboardMonthOption[]>([]);
  readonly loading = signal(false);
  readonly processing = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly syncing = signal(false);
  readonly syncProgressLabel = signal<string | null>(null);
  readonly syncingAll = signal(false);
  readonly syncAllProgress = signal<SyncProgress | null>(null);
  readonly summary = signal<SummaryResponse | null>(null);
  readonly mlStatus = signal<MercadoLivreStatus | null>(null);
  readonly closures = signal<PeriodClosure[]>([]);
  readonly insights = signal<InsightsOverview | null>(null);
  readonly mpAccountMoney = signal<MpAccountMoneyView | null>(null);
  readonly periodBusiness = signal<PeriodBusinessView | null>(null);
  readonly employees = signal<EmployeeConfig[]>([]);
  readonly employeeRateDrafts = signal<Record<string, string>>({});
  readonly savingItem = signal<Record<string, boolean>>({});
  readonly showSettings = signal(false);
  readonly standardWeekHelp = 'Semana padrão = sábado 00:00 até sexta 23:59 (horário de São Paulo).';
  readonly showHelp = signal(false);

  readonly exportUrl = computed(() => this.api.getExportUrl(this.startDate(), this.endDate()));
  readonly activePeriodLabel = computed(() => `${this.formatDate(this.startDate())} até ${this.formatDate(this.endDate())}`);
  readonly selectedMonthOption = computed(() => this.monthOptions().find((month) => month.month === this.selectedMonth()) ?? null);
  readonly monthStatusLabel = computed(() => {
    const selected = this.selectedMonthOption();
    if (!selected) {
      return 'Período manual: leitura feita pelo intervalo de datas escolhido.';
    }
    const parts = [
      selected.hasMlData ? 'dados ML salvos' : 'sem dados ML salvos',
      selected.hasMpData ? 'extrato MP salvo' : 'sem extrato MP salvo'
    ];
    if (selected.lastSyncAt) {
      parts.push(`última sync ${selected.lastSyncAt}`);
    }
    return `${selected.month}: ${parts.join(' · ')}.`;
  });
  readonly isWeekClosed = computed(() => !this.busy() && this.isMpAccountMoneyReady(this.mpAccountMoney()));
  readonly busy = computed(() => this.loading() || this.processing() || this.syncing());

  readonly busyLabel = computed(() => {
    if (this.syncing()) return this.syncProgressLabel() ?? 'Sincronizando dados na API do Mercado Livre...';
    if (this.processing()) return 'Processando conciliação do período...';
    if (this.loading()) return 'Carregando painel...';
    return 'Carregando...';
  });

  constructor(
    private readonly api: ApiService,
    private readonly session: SessionService
  ) {
    void this.bootstrap();
  }

  async bootstrap(): Promise<void> {
    const ok = await this.session.refresh();
    if (!ok) {
      this.error.set('Acesso não autenticado. Entre novamente pelo Cloudflare Access.');
      return;
    }
    await Promise.all([this.loadIntegrationStatus(), this.loadSettings(), this.loadDashboardMonths()]);
    await this.loadCurrentPeriodData();
    this.message.set('Dados carregados. Trocar o período mostra primeiro o que já está salvo; use "Atualizar fechamento" para buscar vendas e dados financeiros.');
  }

  async loadCurrentPeriodData(): Promise<void> {
    this.loading.set(true);
    try {
      await Promise.all([
        this.loadSummary(),
        this.loadClosures(),
        this.loadInsights(),
        this.loadMercadoPagoAccountMoney(),
        this.loadPeriodBusiness()
      ]);
    } finally {
      this.loading.set(false);
    }
  }

  setTab(tab: DashboardTab): void {
    this.tab.set(tab);
    window.setTimeout(() => document.getElementById(`panel-${tab}`)?.focus({ preventScroll: true }));
  }

  handleTabKeydown(event: KeyboardEvent, current: DashboardTab): void {
    const tabs: DashboardTab[] = ['caixa', 'movimentos', 'vendas', 'historico'];
    const currentIndex = tabs.indexOf(current);
    const keyMap: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1
    };

    if (event.key === 'Home') {
      event.preventDefault();
      this.setTab(tabs[0]);
      document.getElementById(`tab-${tabs[0]}`)?.focus();
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      const last = tabs[tabs.length - 1];
      this.setTab(last);
      document.getElementById(`tab-${last}`)?.focus();
      return;
    }

    const direction = keyMap[event.key];
    if (!direction) {
      return;
    }

    event.preventDefault();
    const next = tabs[(currentIndex + direction + tabs.length) % tabs.length];
    this.setTab(next);
    document.getElementById(`tab-${next}`)?.focus();
  }

  toggleHelp(): void {
    this.showHelp.update((value) => !value);
  }

  toggleSettings(): void {
    if (this.showSettings()) {
      this.closeSettings();
      return;
    }
    this.openSettings();
  }

  openSettings(): void {
    this.settingsReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.showSettings.set(true);
    void this.loadSettings();
    window.setTimeout(() => this.firstSettingsFocusable()?.focus());
  }

  closeSettings(): void {
    this.showSettings.set(false);
    window.setTimeout(() => this.settingsReturnFocus?.focus({ preventScroll: true }));
  }

  handleSettingsKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSettings();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const focusable = this.settingsFocusable();
    if (focusable.length === 0) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private firstSettingsFocusable(): HTMLElement | null {
    return this.settingsFocusable()[0] ?? null;
  }

  private settingsFocusable(): HTMLElement[] {
    const panel = this.settingsPanel?.nativeElement;
    if (!panel) {
      return [];
    }

    return Array.from(
      panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
  }

  saleRowLabel(row: { itemTitle?: string | null; itemId?: string | null; operationKey: string }): string {
    return row.itemTitle?.trim() || row.itemId?.trim() || `Operação ${row.operationKey}`;
  }

  setStartDate(value: string): void {
    this.selectedMonth.set('manual');
    this.startDate.set(value);
  }

  setEndDate(value: string): void {
    this.selectedMonth.set('manual');
    this.endDate.set(value);
  }

  async setMonth(value: string): Promise<void> {
    this.selectedMonth.set(value);
    if (value === 'manual') {
      return;
    }
    const month = this.monthOptions().find((item) => item.month === value);
    if (!month) {
      return;
    }
    this.startDate.set(month.startDate);
    this.endDate.set(month.endDate);
    await this.applyPeriod();
  }

  applyQuickRange(kind: 'today' | 'week' | 'month'): void {
    const today = toDateOnly(new Date());
    if (kind === 'today') {
      this.startDate.set(today);
      this.endDate.set(today);
      void this.applyPeriod();
      return;
    }
    if (kind === 'week') {
      this.resetDefaultPeriod();
      return;
    }
    const monthStart = `${today.slice(0, 8)}01`;
    this.startDate.set(monthStart);
    this.endDate.set(today);
    void this.applyPeriod();
  }

  isQuickRange(kind: 'today' | 'week' | 'month'): boolean {
    const today = toDateOnly(new Date());
    if (kind === 'today') {
      return this.startDate() === today && this.endDate() === today;
    }
    if (kind === 'week') {
      return this.startDate() === this.defaultPeriod.startDate && this.endDate() === this.defaultPeriod.endDate;
    }
    const monthStart = `${today.slice(0, 8)}01`;
    return this.startDate() === monthStart && this.endDate() === today;
  }

  resetDefaultPeriod(): void {
    this.selectedMonth.set('manual');
    this.startDate.set(this.defaultPeriod.startDate);
    this.endDate.set(this.defaultPeriod.endDate);
    void this.applyPeriod();
  }

  async applyPeriod(): Promise<void> {
    if (!this.startDate() || !this.endDate()) {
      return;
    }
    this.selectedMonth.set(monthFromDateRange(this.startDate(), this.endDate()));
    const seq = ++this.refreshSeq;
    await this.loadCurrentPeriodData();
    if (seq !== this.refreshSeq) {
      return;
    }
    if (this.isMpAccountMoneyReady(this.mpAccountMoney())) {
      this.message.set('Período carregado com extrato oficial pronto para conferência.');
    } else {
      this.message.set('Período carregado, mas o extrato oficial ainda não está completo. Atualize as informações antes de pagar comissão.');
    }
  }

  async refreshPeriod(): Promise<void> {
    if (!this.startDate() || !this.endDate()) {
      return;
    }
    const seq = ++this.refreshSeq;
    await this.runFullSync(seq, true);
  }

  async syncAll(): Promise<void> {
    if (this.syncingAll()) return;
    this.syncingAll.set(true);
    this.syncAllProgress.set(null);
    this.error.set(null);
    try {
      await runSyncOrchestration(this.api, this.startDate(), this.endDate(), (p) => this.syncAllProgress.set(p), 'financial');
      await this.loadCurrentPeriodData();
      await this.loadDashboardMonths();
      this.message.set('Atualização completa do período concluída.');
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao sincronizar tudo.');
    } finally {
      this.syncingAll.set(false);
    }
  }

  async syncHistoricalFromJanuary(): Promise<void> {
    if (this.syncingAll()) return;
    const now = new Date();
    const endDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    this.syncingAll.set(true);
    this.syncAllProgress.set(null);
    this.error.set(null);
    this.message.set('Carregando histórico completo desde janeiro. Este processo pode levar alguns minutos.');
    try {
      const result = await runSyncOrchestration(
        this.api,
        '2026-01-01',
        endDate,
        (progress) => this.syncAllProgress.set(progress),
        'historical'
      );
      await Promise.all([this.loadCurrentPeriodData(), this.loadDashboardMonths()]);
      this.message.set(
        result.status === 'DONE'
          ? 'Histórico completo desde janeiro carregado.'
          : 'Histórico carregado parcialmente. Veja as fontes pendentes no progresso acima.'
      );
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao carregar o histórico completo.');
    } finally {
      this.syncingAll.set(false);
    }
  }

  formatSyncSource(source: string): string {
    const labels: Record<string, string> = {
      ml_orders: 'Pedidos e pagamentos ML',
      mp_account: 'Extrato Mercado Pago',
      mp_release: 'Dinheiro liberado MP',
      listings: 'Anúncios ativos',
      claims: 'Reclamações e devoluções',
      reputation: 'Reputação',
      visits: 'Visitas por anúncio',
      ads: 'Publicidade'
    };
    return labels[source] ?? source;
  }

  private async runFullSync(seq: number, forceAccountMoney = false): Promise<void> {
    await this.syncAndProcessPeriod(seq, forceAccountMoney);
    if (seq !== this.refreshSeq) {
      return;
    }
    await Promise.all([
      this.loadInsights(),
      this.loadMercadoPagoAccountMoney(),
      this.loadClosures(),
      this.loadPeriodBusiness()
    ]);
    await this.loadDashboardMonths();
  }

  private async syncAndProcessPeriod(seq: number, forceAccountMoney = false): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    this.message.set('Atualizando período automaticamente...');
    this.processing.set(true);
    this.syncing.set(true);
    this.syncProgressLabel.set('Preparando sincronização do período...');
    try {
      const initialMpStatus = await this.primeMercadoPagoAccountMoney(seq, forceAccountMoney);
      if (seq !== this.refreshSeq) {
        return;
      }
      await this.runIncrementalSync(seq);
      if (seq !== this.refreshSeq) {
        return;
      }
      const mpCashReady = initialMpStatus || (await this.runMercadoPagoAccountMoneySync(seq));
      if (seq !== this.refreshSeq) {
        return;
      }
      this.syncProgressLabel.set('Sincronização concluída. Processando conciliação...');
      await this.api.processPeriod(this.startDate(), this.endDate());
      if (seq !== this.refreshSeq) {
        return;
      }
      this.message.set(
        mpCashReady
          ? 'Período atualizado com sucesso, incluindo extrato oficial Mercado Pago.'
          : 'Período atualizado. O Mercado Pago ainda está gerando o extrato oficial de caixa; aplique o período novamente em alguns instantes.'
      );
    } catch (error: any) {
      if (seq !== this.refreshSeq) {
        return;
      }
      this.error.set(error?.error?.error ?? 'Falha ao atualizar dados do período pela API Mercado Livre.');
    } finally {
      if (seq === this.refreshSeq) {
        this.loading.set(false);
      }
      this.syncing.set(false);
      this.processing.set(false);
      this.syncProgressLabel.set(null);
    }
  }

  private async primeMercadoPagoAccountMoney(seq: number, force = false): Promise<boolean> {
    this.syncProgressLabel.set('Abrindo a geração dos relatórios Mercado Pago...');
    if (seq !== this.refreshSeq) {
      return false;
    }
    const result = await this.api.syncMercadoPagoAccountMoney(this.startDate(), this.endDate(), force);
    const releaseResult = await this.api.syncMercadoPagoReleaseReport(this.startDate(), this.endDate(), force);
    if (result.status === 'DONE' && releaseResult.status === 'DONE') {
      this.syncProgressLabel.set(`Mercado Pago importado: extrato (${result.rowsImported}) e dinheiro liberado (${releaseResult.rowsImported}).`);
      return true;
    }
    if (result.status === 'FAILED' || result.ok === false) {
      throw new Error(result.message || 'Falha ao gerar extrato Mercado Pago.');
    }
    if (releaseResult.status === 'FAILED' || releaseResult.ok === false) {
      throw new Error(releaseResult.message || 'Falha ao gerar relatório de dinheiro liberado Mercado Pago.');
    }
    this.syncProgressLabel.set('Relatórios Mercado Pago solicitados. Sincronizando Mercado Livre enquanto eles são gerados...');
    return false;
  }

  private async runIncrementalSync(seq: number): Promise<void> {
    const started = await this.api.startMercadoLivreSync(this.startDate(), this.endDate());
    let job = started.job;
    this.syncProgressLabel.set(this.buildSyncProgressLabel(job));

    let safety = 0;
    while (job.status === 'RUNNING' && !job.done) {
      if (seq !== this.refreshSeq) {
        return;
      }
      safety += 1;
      if (safety > 400) {
        throw new Error('Sincronização demorou além do esperado. Tente novamente.');
      }
      const stepped = await this.api.stepMercadoLivreSync(job.jobId);
      job = stepped.job;
      this.syncProgressLabel.set(this.buildSyncProgressLabel(job));
    }

    if (job.status === 'FAILED') {
      throw new Error(job.message || 'Sincronização falhou.');
    }
  }

  private async runMercadoPagoAccountMoneySync(seq: number): Promise<boolean> {
    this.syncProgressLabel.set('Solicitando extrato e dinheiro liberado Mercado Pago...');
    if (seq !== this.refreshSeq) {
      return false;
    }
    const result = await this.api.syncMercadoPagoAccountMoney(this.startDate(), this.endDate());
    const releaseResult = await this.api.syncMercadoPagoReleaseReport(this.startDate(), this.endDate());
    if (result.status === 'DONE' && releaseResult.status === 'DONE') {
      this.syncProgressLabel.set(`Mercado Pago importado: extrato (${result.rowsImported}) e dinheiro liberado (${releaseResult.rowsImported}).`);
      return true;
    }
    if (result.status === 'FAILED' || result.ok === false) {
      throw new Error(result.message || 'Falha ao gerar extrato Mercado Pago.');
    }
    if (releaseResult.status === 'FAILED' || releaseResult.ok === false) {
      throw new Error(releaseResult.message || 'Falha ao gerar relatório de dinheiro liberado Mercado Pago.');
    }

    this.syncProgressLabel.set('Mercado Pago ainda está gerando os relatórios oficiais. Você pode aplicar o período novamente em instantes.');
    return false;
  }

  private buildSyncProgressLabel(job: SyncJobProgress): string {
    const p = job.progress;
    return `Sincronizando ${p.percent}% · Pedidos ${p.doneOrders}/${p.totalOrders} · Meses ${p.doneMonths}/${p.totalMonths}`;
  }

  async loadSummary(): Promise<void> {
    try {
      const summary = await this.api.getSummary(this.startDate(), this.endDate());
      this.summary.set(summary);
    } catch {
      this.summary.set(null);
    }
  }

  async loadIntegrationStatus(): Promise<void> {
    try {
      this.mlStatus.set(await this.api.getMercadoLivreStatus());
    } catch {
      this.mlStatus.set(null);
    }
  }

  async loadDashboardMonths(): Promise<void> {
    try {
      const data = await this.api.getDashboardMonths();
      this.monthOptions.set(data.months);
    } catch {
      this.monthOptions.set([]);
    }
  }

  async connectMercadoLivre(): Promise<void> {
    this.error.set(null);
    try {
      const response = await this.api.getMercadoLivreAuthorizationUrl();
      window.location.href = response.authorizationUrl;
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Configure as keys Mercado Livre antes de conectar.');
    }
  }

  async loadClosures(): Promise<void> {
    try {
      const response = await this.api.listClosures();
      this.closures.set(response.closures);
    } catch {
      this.closures.set([]);
    }
  }

  async loadInsights(): Promise<void> {
    try {
      const data = await this.api.getInsightsOverview(subtractOneYear(this.endDate()), this.endDate());
      this.insights.set(data);
    } catch {
      this.insights.set(null);
    }
  }

  async loadMercadoPagoAccountMoney(): Promise<void> {
    try {
      const data = await this.api.getMercadoPagoAccountMoney(this.startDate(), this.endDate());
      this.mpAccountMoney.set(data);
    } catch {
      this.mpAccountMoney.set(null);
    }
  }

  async loadPeriodBusiness(): Promise<void> {
    try {
      const data = await this.api.getPeriodBusinessView(this.startDate(), this.endDate());
      this.periodBusiness.set(data);
    } catch {
      this.periodBusiness.set(null);
    }
  }

  async loadSettings(): Promise<void> {
    try {
      const employeesData = await this.api.listEmployees();
      const employees = employeesData.employees.length > 0 ? employeesData.employees : DEFAULT_EMPLOYEES;
      this.employees.set(employees);

      const rateDrafts: Record<string, string> = {};
      for (const employee of employees) {
        rateDrafts[employee.name] = (Number(employee.commission_rate ?? 0) * 100).toFixed(2);
      }
      this.employeeRateDrafts.set(rateDrafts);
    } catch {
      this.employees.set(DEFAULT_EMPLOYEES);
      this.employeeRateDrafts.set({
        Rodrigo: '3.00',
        Maria: '1.00'
      });
    }
  }

  setEmployeeRateDraft(name: string, value: string): void {
    this.employeeRateDrafts.set({ ...this.employeeRateDrafts(), [name]: value });
  }

  employeeRate(name: string, fallback: number): number {
    const normalized = name.trim().toLowerCase();
    return this.employees().find((employee) => employee.name.trim().toLowerCase() === normalized)?.commission_rate ?? fallback;
  }

  async saveEmployeeRate(employee: EmployeeConfig): Promise<void> {
    const draftPct = Number(this.employeeRateDrafts()[employee.name]);
    if (!Number.isFinite(draftPct) || draftPct < 0 || draftPct > 100) {
      this.error.set('Percentual de comissão inválido. Use um valor entre 0 e 100.');
      return;
    }

    this.savingItem.set({ ...this.savingItem(), [`emp:${employee.name}`]: true });
    this.error.set(null);
    try {
      await this.api.upsertEmployee({
        name: employee.name,
        pixKey: employee.pix_key,
        commissionRate: draftPct / 100,
        calculationBase: employee.calculation_base,
        status: employee.status
      });
      await this.loadSettings();
      await this.api.processPeriod(this.startDate(), this.endDate());
      await this.loadSummary();
      this.message.set(`Comissão de ${employee.name} atualizada.`);
    } catch (error: any) {
      this.error.set(error?.error?.error ?? 'Falha ao salvar comissão.');
    } finally {
      this.savingItem.set({ ...this.savingItem(), [`emp:${employee.name}`]: false });
    }
  }

  formatReason(reason: string | null | undefined): string {
    const normalized = (reason ?? '').trim().toLowerCase();
    if (!normalized || normalized === 'sem_motivo') return 'Sem motivo informado pela plataforma';

    const map: Record<string, string> = {
      accredited: 'Pagamento aprovado',
      refunded: 'Pagamento estornado',
      cancelled_by_user: 'Cancelado pelo comprador',
      arrependimento_do_cliente: 'Cliente desistiu da compra',
      devolucao_por_qualidade: 'Devolução por qualidade do produto',
      cc_rejected_call_for_authorize: 'Cartão recusado (falta de autorização)'
    };

    if (map[normalized]) return map[normalized];
    return normalized.replaceAll('_', ' ');
  }

  formatStatus(status: string | null | undefined): string {
    const normalized = (status ?? '').trim().toLowerCase();
    const map: Record<string, string> = {
      vendido: 'Vendido',
      recebido: 'Recebido',
      cancelado: 'Cancelado',
      devolvido: 'Devolvido',
      approved: 'Aprovado',
      cancelled: 'Cancelado',
      refunded: 'Estornado'
    };
    return map[normalized] ?? (status || '-');
  }

  formatMpAccountCategory(category: string | null | undefined): string {
    const normalized = (category ?? '').trim().toLowerCase();
    const map: Record<string, string> = {
      dinheiro_disponivel_comissao: 'Venda liberada (entra na comissão)',
      ajuste_credito_comissao: 'Crédito/ajuste operacional (volta para a comissão)',
      devolucao_estorno_comissao: 'Devolução/estorno (sai da comissão)',
      dinheiro_liberado_vendas: 'Dinheiro liberado de vendas',
      devolucoes_reclamacoes: 'Devoluções, reembolsos e reclamações',
      envios_frete: 'Envios e fretes Mercado Livre',
      publicidade: 'Publicidade / campanhas',
      faturas_ml_mp: 'Faturas e débitos ML/MP',
      retencoes_liberacoes: 'Retenções e liberações',
      saques_transferencias: 'Saques, Pix enviados e transferências',
      transferencias_entradas: 'Pix recebidos / entradas externas',
      outros_debitos: 'Outros débitos operacionais',
      outros: 'Outros movimentos'
    };
    return map[normalized] ?? this.humanizeTechnicalCode(category ?? '');
  }

  mpCategoryToneClass(category: string | null | undefined): string {
    const normalized = (category ?? '').trim().toLowerCase();
    if (normalized.includes('devol') || normalized.includes('reclam')) return 'tone-return';
    if (normalized.includes('ajuste_credito')) return 'tone-success';
    if (normalized.includes('envio') || normalized.includes('frete')) return 'tone-fixed';
    if (normalized.includes('public')) return 'tone-ads';
    if (normalized.includes('fatura') || normalized.includes('debito')) return 'tone-fee';
    if (normalized.includes('saque') || normalized.includes('transfer')) return 'tone-neutral';
    if (normalized.includes('dinheiro_liberado') || normalized.includes('dinheiro_disponivel')) return 'tone-success';
    return 'tone-neutral';
  }

  isNonOperationalMpCategory(category: string | null | undefined): boolean {
    const normalized = (category ?? '').trim().toLowerCase();
    return normalized === 'saques_transferencias' || normalized === 'transferencias_entradas';
  }

  cashOutCategories(cash: MpAccountMoneyView): MpAccountMoneyView['categories'] {
    return cash.categories.filter((row) => row.exits > 0).sort((a, b) => b.exits - a.exits);
  }

  cashInCategories(cash: MpAccountMoneyView): MpAccountMoneyView['categories'] {
    return cash.categories.filter((row) => row.entries > 0).sort((a, b) => b.entries - a.entries);
  }

  categoryNet(cash: MpAccountMoneyView, category: string): number {
    return cash.categories.find((row) => row.category === category)?.net ?? 0;
  }

  categoryExits(cash: MpAccountMoneyView, category: string): number {
    return cash.categories.find((row) => row.category === category)?.exits ?? 0;
  }

  commissionDebitTotal(cash: MpAccountMoneyView): number {
    return this.categoryExits(cash, 'devolucao_estorno_comissao');
  }

  attentionMovements(cash: MpAccountMoneyView): MpAccountMoneyView['attentionMovements'] {
    return cash.attentionMovements.slice(0, 12);
  }

  goodSales(cash: MpAccountMoneyView): MpAccountMoneyView['salesHighlights'] {
    return cash.salesHighlights.filter((row) => row.entries > 0).slice(0, 8);
  }

  printGoodSales(cash: MpAccountMoneyView): MpAccountMoneyView['salesHighlights'] {
    return cash.salesHighlights.filter((row) => row.entries > 0);
  }

  printAttentionMovements(cash: MpAccountMoneyView): MpAccountMoneyView['attentionMovements'] {
    return cash.attentionMovements.slice(0, 12);
  }

  isMpAccountMoneyReady(cash: MpAccountMoneyView | null): boolean {
    return cash?.job?.status === 'DONE';
  }

  movementAdjustment(row: { transactionAmount: number; amount: number }): number {
    const gross = row.transactionAmount;
    const net = row.amount;
    if (gross === 0 || net === gross) return 0;
    return Math.round((net - gross) * 100) / 100;
  }

  movementSimpleExplanation(category: string | null | undefined, amount: number): string {
    const normalized = (category ?? '').trim().toLowerCase();
    if (normalized === 'dinheiro_disponivel_comissao') return 'Venda cujo dinheiro caiu no Mercado Pago no período. Entra na base da comissão.';
    if (normalized === 'ajuste_credito_comissao') return 'Crédito operacional do Mercado Pago, como liberação de reserva ou ajuste de estorno. Volta para a base da comissão.';
    if (normalized === 'devolucao_estorno_comissao') return 'Venda cancelada, devolvida ou estornada cujo dinheiro saiu do caixa no período. Reduz a base da comissão.';
    if (normalized === 'dinheiro_liberado_vendas') return 'Movimento do extrato Mercado Pago usado para conferência; a comissão usa o relatório de dinheiro liberado.';
    if (normalized === 'devolucoes_reclamacoes') return 'Cliente devolveu, reclamou ou houve estorno. Reduz a base de comissão.';
    if (normalized === 'envios_frete' && amount > 0) return 'Ajuste positivo ligado a venda aprovada e já liberada.';
    if (normalized === 'envios_frete') return 'Custo de envio/frete lançado pelo Mercado Livre.';
    if (normalized === 'publicidade') return 'Campanhas, descontos ou publicidade que saíram do resultado.';
    if (normalized === 'faturas_ml_mp') return 'Débito de fatura, tarifa ou ajuste cobrado pelo ML/MP.';
    if (normalized === 'saques_transferencias') return 'Dinheiro saiu da conta, mas não é custo da venda.';
    if (normalized === 'transferencias_entradas') return 'Entrada financeira externa. Não entra na comissão da venda.';
    if (amount < 0) return 'Saída de dinheiro que precisa ser conferida no fechamento.';
    return 'Movimento registrado no Mercado Pago.';
  }

  formatMpTransactionType(type: string | null | undefined): string {
    const normalized = (type ?? '').trim().toUpperCase();
    const lowered = (type ?? '').trim().toLowerCase();
    const releaseMap: Record<string, string> = {
      release: 'Liberação no MP',
      payment: 'Pagamento',
      mediation: 'Mediação / reclamação',
      reserve_for_dispute: 'Reserva por disputa',
      reserve_for_payment: 'Reserva do pagamento',
      refund: 'Reembolso',
      chargeback: 'Estorno'
    };
    if (releaseMap[lowered]) return releaseMap[lowered];
    const map: Record<string, string> = {
      SETTLEMENT: 'Venda recebida',
      SETTLEMENT_SHIPPING: 'Frete da venda',
      DISPUTE: 'Disputa / reclamação',
      REFUND: 'Reembolso ao cliente',
      CHARGEBACK: 'Estorno (chargeback)',
      PAYOUT: 'Saque / transferência',
      PAYOUTS: 'Saque / transferência',
      WITHDRAWAL: 'Saque',
      RESERVE_FOR_DISPUTE: 'Valor retido por disputa',
      RESERVE_FOR_BPP_SHIPPING_RETURN: 'Retido por devolução de frete',
      CREDIT_PAYMENT: 'Crédito recebido',
      TAX: 'Imposto',
      FEE: 'Tarifa'
    };
    if (map[normalized]) return map[normalized];
    if (!normalized) return 'Movimento';
    return normalized.charAt(0) + normalized.slice(1).toLowerCase().replaceAll('_', ' ');
  }

  mpMovementPlain(row: { transactionType?: string | null; description?: string | null; category: string; amount: number; transactionAmount?: number }): string {
    const type = (row.transactionType ?? '').trim().toUpperCase();
    const description = (row.description ?? '').trim().toLowerCase();
    const adj = row.transactionAmount !== undefined ? this.movementAdjustment({ transactionAmount: row.transactionAmount, amount: row.amount }) : 0;
    if (row.category === 'dinheiro_disponivel_comissao' && row.amount > 0) return 'Venda liberada no Mercado Pago. O valor líquido já desconta tarifas, frete, financiamento e impostos informados no relatório.';
    if (row.category === 'devolucao_estorno_comissao' && description === 'mediation') return 'Mediação/reclamação debitada no dinheiro disponível. Reduz a base da comissão.';
    if (row.category === 'devolucao_estorno_comissao' && description === 'reserve_for_dispute') return 'Valor reservado por disputa no Mercado Pago. Reduz a base enquanto estiver retido.';
    if (row.category === 'devolucao_estorno_comissao' && description === 'reserve_for_payment') return 'Reserva/ajuste de pagamento que saiu do dinheiro disponível. Reduz a base.';
    if (row.category === 'devolucao_estorno_comissao' && description === 'payment') return 'Débito de pagamento lançado no relatório de dinheiro liberado. Reduz a base da comissão.';
    if (type === 'SETTLEMENT' && row.amount >= 0) return 'Dinheiro de uma venda que foi liberado para a conta.';
    if (type === 'SETTLEMENT_SHIPPING' && row.amount >= 0) return 'Ajuste positivo vinculado a uma venda cujo dinheiro já foi liberado.';
    if (type === 'SETTLEMENT' && row.amount < 0) return 'Ajuste negativo sobre uma venda (estorno parcial ou desconto).';
    if (type === 'DISPUTE' && adj !== 0) return `Cliente abriu disputa/reclamação. Débito bruto de ${this.apiCurrency(row.transactionAmount ?? 0)}, com ajuste de ${this.apiCurrency(adj)}. Impacto líquido: ${this.apiCurrency(row.amount)}.`;
    if (type === 'DISPUTE') return 'Cliente abriu disputa/reclamação. O valor foi debitado ou retido.';
    if (type === 'REFUND' && adj !== 0) return `Reembolso ao cliente. Débito bruto de ${this.apiCurrency(row.transactionAmount ?? 0)}, com ajuste de ${this.apiCurrency(adj)}. Impacto líquido: ${this.apiCurrency(row.amount)}.`;
    if (type === 'REFUND') return 'Reembolso devolvido ao cliente. Saiu da conta.';
    if (type === 'CHARGEBACK' && adj !== 0) return `Estorno pelo banco. Débito bruto de ${this.apiCurrency(row.transactionAmount ?? 0)}, com ajuste de ${this.apiCurrency(adj)}. Impacto líquido: ${this.apiCurrency(row.amount)}.`;
    if (type === 'CHARGEBACK') return 'Estorno solicitado pelo banco/cliente. Saiu da conta.';
    if (type === 'PAYOUT' || type === 'PAYOUTS' || type === 'WITHDRAWAL') return 'Você sacou/transferiu dinheiro. Não é custo de venda.';
    return this.movementSimpleExplanation(row.category, row.amount);
  }

  private apiCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  }

  productKind(product: { received?: number; grossRevenue?: number; returnCostAmount?: number; fixedCostAmount?: number; adCostAmount?: number; netResult?: number }): 'ativa' | 'devolvida' | 'fatura' {
    if ((product.returnCostAmount ?? 0) > 0) return 'devolvida';
    if ((product.grossRevenue ?? 0) === 0 && (product.received ?? 0) === 0 && ((product.fixedCostAmount ?? 0) > 0 || (product.adCostAmount ?? 0) > 0)) {
      return 'fatura';
    }
    return 'ativa';
  }

  activeProducts(biz: PeriodBusinessView): PeriodBusinessView['productEconomics'] {
    return biz.productEconomics.filter((p) => this.productKind(p) === 'ativa');
  }

  returnedProducts(biz: PeriodBusinessView): PeriodBusinessView['productEconomics'] {
    return biz.productEconomics.filter((p) => this.productKind(p) === 'devolvida');
  }

  feeOnlyProducts(biz: PeriodBusinessView): PeriodBusinessView['productEconomics'] {
    return biz.productEconomics.filter((p) => this.productKind(p) === 'fatura');
  }

  marginPct(netResult: number, soldAmount: number): number | null {
    if (soldAmount <= 0) return null;
    return Math.round((netResult / soldAmount) * 10000) / 100;
  }

  profitPct(netResult: number, soldAmount: number): number | null {
    const pct = this.marginPct(netResult, soldAmount);
    return pct !== null && pct > 0 ? pct : null;
  }

  lossPct(netResult: number, soldAmount: number): number | null {
    const pct = this.marginPct(netResult, soldAmount);
    return pct !== null && pct < 0 ? Math.abs(pct) : null;
  }

  groupedSales(cash: MpAccountMoneyView): MpAccountMoneyView['salesHighlights'] {
    return [...cash.salesHighlights].sort((a, b) => b.net - a.net);
  }

  commissionCreditMovements(cash: MpAccountMoneyView): MpAccountMoneyView['recentMovements'] {
    return cash.recentMovements.filter((row) => row.amount > 0 && row.category === 'dinheiro_disponivel_comissao' && row.isReleased === 1);
  }

  commissionDebitMovements(cash: MpAccountMoneyView): MpAccountMoneyView['recentMovements'] {
    return cash.recentMovements.filter((row) => row.amount < 0 && row.category === 'devolucao_estorno_comissao');
  }

  commissionAdjustmentGroups(cash: MpAccountMoneyView): CommissionAdjustmentGroup[] {
    const rows = cash.recentMovements.filter((row) => row.category === 'devolucao_estorno_comissao' || row.category === 'ajuste_credito_comissao');
    const groups = new Map<string, CommissionAdjustmentGroup & { reasonSet: Set<string> }>();

    for (const row of rows) {
      const key = row.linkedOrderId ?? row.sourceId ?? `${row.settlementDate ?? 'sem-data'}:${row.description ?? 'sem-tipo'}`;
      const existing =
        groups.get(key) ??
        {
          key,
          orderId: row.linkedOrderId ?? null,
          sourceId: row.sourceId ?? null,
          itemTitle: row.linkedItemTitle ?? null,
          lastSettlementDate: row.settlementDate ?? null,
          rowsCount: 0,
          debits: 0,
          credits: 0,
          net: 0,
          reasons: '',
          reasonSet: new Set<string>()
        };

      existing.rowsCount += 1;
      existing.debits += row.amount < 0 ? Math.abs(Number(row.amount ?? 0)) : 0;
      existing.credits += row.amount > 0 ? Number(row.amount ?? 0) : 0;
      existing.net += Number(row.amount ?? 0);
      existing.itemTitle = existing.itemTitle ?? row.linkedItemTitle ?? null;
      existing.orderId = existing.orderId ?? row.linkedOrderId ?? null;
      existing.sourceId = existing.sourceId ?? row.sourceId ?? null;
      if ((row.settlementDate ?? '') > (existing.lastSettlementDate ?? '')) existing.lastSettlementDate = row.settlementDate;
      existing.reasonSet.add(this.formatMpTransactionType(row.description ?? row.transactionType));
      groups.set(key, existing);
    }

    return [...groups.values()]
      .map(({ reasonSet, ...group }) => ({
        ...group,
        debits: Math.round(group.debits * 100) / 100,
        credits: Math.round(group.credits * 100) / 100,
        net: Math.round(group.net * 100) / 100,
        reasons: [...reasonSet].join(', ')
      }))
      .filter((group) => group.debits > 0 || group.credits > 0)
      .sort((a, b) => a.net - b.net);
  }

  reconciliationMovements(cash: MpAccountMoneyView): MpAccountMoneyView['recentMovements'] {
    return cash.recentMovements.filter((row) => !row.linkedOrderId);
  }

  visibleMovementsNet(rows: Array<{ amount: number }>): number {
    return Math.round(rows.reduce((acc, row) => acc + Number(row.amount ?? 0), 0) * 100) / 100;
  }

  visibleGroupsNet(rows: Array<{ net: number }>): number {
    return Math.round(rows.reduce((acc, row) => acc + Number(row.net ?? 0), 0) * 100) / 100;
  }

  visibleMovementsGross(rows: Array<{ transactionAmount: number }>): number {
    return Math.round(rows.reduce((acc, row) => acc + Number(row.transactionAmount ?? 0), 0) * 100) / 100;
  }

  linkedSaleLabel(row: { linkedOrderId?: string | null; linkedItemTitle?: string | null; sourceId?: string | null }): string {
    if (row.linkedItemTitle) return row.linkedItemTitle;
    if (row.linkedOrderId) return `Pedido ${row.linkedOrderId}`;
    if (row.sourceId) return `ID MP ${row.sourceId}`;
    return 'Movimento sem venda vinculada';
  }

  formatListingType(listingTypeId: string | null | undefined): string {
    const normalized = (listingTypeId ?? '').trim().toLowerCase();
    const map: Record<string, string> = {
      gold_pro: 'Premium',
      gold_special: 'Clássico',
      free: 'Grátis'
    };
    return map[normalized] ?? (listingTypeId || '-');
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const cleaned = value.replace('T', ' ').replace(/\.\d{3}.*$/, '').replace(/\+.*$/, '').trim();
    const parts = cleaned.split(' ')[0]?.split('-') ?? [];
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return value;
  }

  formatMonth(value: string | null | undefined): string {
    if (!value) return '-';
    const parts = value.split('-');
    if (parts.length >= 2) return `${parts[1]}/${parts[0]}`;
    return value;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '-';
    const cleaned = value.replace('T', ' ').replace(/\.\d{3}.*$/, '').replace(/\+.*$/, '').trim();
    const [datePart, timePart] = cleaned.split(' ');
    const dateParts = datePart?.split('-') ?? [];
    if (dateParts.length === 3) {
      const date = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      const time = timePart ? ` ${timePart.slice(0, 5)}` : '';
      return `${date}${time}`;
    }
    return value;
  }

  private humanizeTechnicalCode(value: string): string {
    const normalized = value.trim().replaceAll('-', '_');
    if (!normalized) return 'Sem descrição';
    return normalized
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  logout(): void {
    window.location.href = '/cdn-cgi/access/logout?returnTo=' + encodeURIComponent(window.location.origin);
  }

  // ── Gráfico do Histórico ──
  readonly chartData = computed(() => {
    const monthly = this.insights()?.monthly ?? [];
    if (monthly.length === 0) return [];

    const maxVal = Math.max(
      ...monthly.map((m) => Math.max(Number(m.totalReceived ?? 0), Number(m.totalDebits ?? 0)))
    );
    const safeMax = maxVal || 1;
    const barMaxHeight = 160;

    return monthly.map((m) => {
      const entrada = Number(m.totalReceived ?? 0);
      const saida = Number(m.totalDebits ?? 0);
      const net = Number(m.netResult ?? 0);
      return {
        label: this.formatMonth(m.month),
        entrada,
        saida,
        net,
        ePct: (entrada / safeMax) * barMaxHeight,
        sPct: (saida / safeMax) * barMaxHeight,
        nPct: (Math.abs(net) / safeMax) * barMaxHeight,
        netPositive: net >= 0
      };
    });
  });

  readonly chartMaxValue = computed(() => {
    const data = this.chartData();
    if (data.length === 0) return 0;
    return Math.max(...data.map((d) => Math.max(d.entrada, d.saida)));
  });

  historyAttentionText(): string {
    const monthly = this.insights()?.monthly ?? [];
    if (monthly.length === 0) return 'Sem histórico suficiente para comparar os meses.';
    const negative = monthly.filter((row) => Number(row.netResult ?? 0) < 0);
    if (negative.length > 0) {
      return `${negative.length} mês(es) fecharam com saída maior que entrada.`;
    }
    return 'Nenhum mês negativo no período.';
  }

  historyNegativeMonths(): number {
    return (this.insights()?.monthly ?? []).filter((row) => Number(row.netResult ?? 0) < 0).length;
  }

  historyBestMonth(): string {
    const monthly = this.insights()?.monthly ?? [];
    if (monthly.length === 0) return '-';
    return monthly.reduce((best, row) => (Number(row.totalReceived ?? 0) > Number(best.totalReceived ?? 0) ? row : best), monthly[0]).month;
  }

  printReport(): void {
    this.printGeneratedAt.set(new Date());
    window.setTimeout(() => window.print());
  }
}
